import {
  InternalTransferMethodDisplayEnum,
  InternalTransferMethodEnum,
  InternalTransferTypeEnum,
} from '@interfaces';
import {
  CurrentInputAccount,
  ISelectedAccount,
  IUserInputAccount,
  Position,
  YesNoEmpty,
} from '@interfaces/models';
import { AvgCostConversion, MinTaxConversion } from '@interfaces/models/spec-id-fallback.enum';
import { OneTimePasscodeStatus } from '@interfaces/one-time-passcode/otp.interface';
import {
  MultiITFrom,
  MultiITState,
  MultiITTo,
  MultiITToAccount,
  MultiITTriage,
} from '@interfaces/state';
import { createSelector } from '@ngrx/store';
import { isOption } from '@shared/helpers';
import { AccountTypes, CostBasisMethods } from '@shared/models';
import { CostBasisPosition } from '@shared/services';
import { GiftingRedirectModalContent } from '@shared/services/content/content-jsons/gifting-redirect-modal-content';
import { getEstimatedTotalTransferValue, toNumber } from '@sharedUtils';
import { getConfigClientFromRTE } from '@src/app/core/config/config-client';
import { getCwpCashPlusSweepTransferToggle } from '@store/reducers/feature/feature.reducer';
import { getOTPStatus } from '@store/reducers/otp/otp.reducer';
import {
  CONTENT_STYLE_BOLD,
  CONTENT_STYLE_BOLD_TICKER,
  CONTENT_STYLE_SMALL,
  CONTENT_STYLE_XSMALL,
  DETAIL_TYPE_TEXT,
} from '@store/selectors/product-list-table-content-style';
import type {
  DetailTypes,
  ProductListTableCell,
  ProductListTableRow,
} from '@vanguard/trade-ui-components-lib-ng-18';

import * as fromFeature from '../../reducers/index';
import { selectCwpMultiItMoToggle } from '../../selectors/feature/feature.selectors';
import { multiITToAdapter } from '../reducers/multi-internal-transfer.reducer';
import {
  calculatePositionsSubtotal,
  hasAccountData,
  isCashPlusEligiblePosition,
} from './multi-internal-transfer.helpers';

export type GiftingRedirectVariant = 'mixed' | 'giftsOnly';

/** The From/To account-type combinations measured by the cash sweep Adobe tag. */
export type CashSweepSegment = 'VBA_to_Cash_Plus' | 'Cash_Plus_to_VBA' | 'Cash_Plus_to_Cash_Plus';

type CashSweepRecipientType = 'cashPlus' | 'vba';

export interface GiftingRedirectModalViewModel {
  variant: GiftingRedirectVariant;
  title: string;
  bodyText: string;
  body2?: string;
  linkText?: string;
  giftList: string[];
  primaryButton: string;
  closeButton: string;
}

// Cached formatters for selector recomputations
const currencyFormatter = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });
const sharesFormatter = new Intl.NumberFormat('en-US', { maximumFractionDigits: 4 });

// Type guard to narrow CurrentInputAccount to IUserInputAccount (differentOwner entry)
const isUserInputAccount = (account: CurrentInputAccount): account is IUserInputAccount =>
  'accountName' in account &&
  typeof account.accountName === 'string' &&
  typeof account.accountNumber === 'string';

export const selectMultiITState = createSelector(
  fromFeature.getMultiITState,
  (state): MultiITState => state,
);

// Triage Selectors
export const selectMultiITTriage = createSelector(
  selectMultiITState,
  (state): MultiITTriage => state.triage,
);

export const selectMultiITTriageTransferTo = createSelector(
  selectMultiITTriage,
  (triage): AccountTypes[] => triage.transferTo,
);

export const selectMultiITTriageTransferFrom = createSelector(
  selectMultiITTriage,
  (triage): AccountTypes => triage.transferFrom,
);

export const selectMultiITTriageIsDivorce = createSelector(
  selectMultiITTriage,
  (triage): YesNoEmpty | undefined => triage.isDivorceTransfer,
);

// To Selectors
export const selectMultiITToState = createSelector(
  selectMultiITState,
  (state): MultiITTo => state.transferTo,
);

export const {
  selectIds: selectMultiITToIds,
  selectEntities: selectMultiITToEntities,
  selectAll: selectAllMultiITToAccounts,
  selectTotal: selectMultiITTotalRecipients,
} = multiITToAdapter.getSelectors(selectMultiITToState);

export const selectMultiITToById = (id: string) =>
  createSelector(selectMultiITToEntities, (entities): MultiITToAccount | undefined => entities[id]);

export const selectMultiITToByIdPositions = (id: string) =>
  createSelector(
    selectMultiITToById(id),
    (account): CostBasisPosition[] | undefined => account?.positions,
  );

export const selectGiftToAccounts = createSelector(
  selectAllMultiITToAccounts,
  (accounts): MultiITToAccount[] => accounts.filter((account) => account.isGift === YesNoEmpty.YES),
);

export const selectHasGiftToAccounts = createSelector(
  selectGiftToAccounts,
  (giftAccounts): boolean => giftAccounts.length > 0,
);

// Gifts-only means every to-account is a gift. Same-owner/new/existing/org accounts
// are stored as EMPTY (never NO), so an EMPTY account is a real non-gift transfer and
// makes the selection mixed, not gifts-only.
export const selectIsGiftsOnly = createSelector(
  selectAllMultiITToAccounts,
  selectHasGiftToAccounts,
  (accounts, hasGifts): boolean =>
    hasGifts && accounts.every((account) => account.isGift === YesNoEmpty.YES),
);

export const selectGiftingRedirectModalContent = createSelector(
  selectIsGiftsOnly,
  selectGiftToAccounts,
  (isGiftsOnly, giftAccounts): GiftingRedirectModalViewModel => {
    const closeButton = GiftingRedirectModalContent.closeButton;

    if (isGiftsOnly) {
      const content = GiftingRedirectModalContent.giftsOnly;
      return {
        variant: 'giftsOnly',
        title: content.title,
        bodyText: content.bodyText,
        giftList: [],
        primaryButton: content.primaryButton,
        closeButton,
      };
    }

    const content = GiftingRedirectModalContent.mixed;
    const giftList = giftAccounts
      .map((account) => account.selectedAccount)
      .filter(isUserInputAccount)
      .map((selected) => `${selected.accountName} — ${selected.accountNumber}`);

    return {
      variant: 'mixed',
      title: content.title,
      bodyText: content.bodyText,
      body2: content.body2,
      linkText: content.linkText,
      giftList,
      primaryButton: content.primaryButton,
      closeButton,
    };
  },
);

export const selectAllMultiITToSelectedAccountIds = createSelector(
  selectAllMultiITToAccounts,
  (accounts): string[] =>
    accounts
      .map((account) => account.selectedAccount)
      .filter(hasAccountData)
      .map((selectedAccount) => selectedAccount.accountData.accountId),
);

export const selectAllMultiITToSelectedAccountIdsExcluding = (excludeTransactionId: string) =>
  createSelector(selectAllMultiITToAccounts, (accounts): string[] =>
    accounts.reduce<string[]>((accountIds, account) => {
      if (
        account.transactionId !== excludeTransactionId &&
        hasAccountData(account.selectedAccount)
      ) {
        accountIds.push(account.selectedAccount.accountData.accountId);
      }
      return accountIds;
    }, []),
  );

// From Selectors
export const selectMultiITFrom = createSelector(
  selectMultiITState,
  (state): MultiITFrom => state.transferFrom,
);

export const selectMultiITFromSelectedAccount = createSelector(
  selectMultiITFrom,
  (from): ISelectedAccount | null => from.selectedAccount,
);

export const selectMultiITFromSelectedHoldings = createSelector(
  selectMultiITFrom,
  (from): CostBasisPosition[] => from.selectedHoldings,
);

export const selectMultiITFromTransferMethod = createSelector(
  selectMultiITFrom,
  (from): InternalTransferMethodEnum => from.transferMethod,
);

export const selectMultiITTransferMethodIsPercent = createSelector(
  selectMultiITFromTransferMethod,
  (transferMethod): boolean => transferMethod === InternalTransferMethodEnum.PERCENT,
);

export const selectMultiITFromSplitEvenly = createSelector(
  selectMultiITFrom,
  (from): boolean => from.splitEvenly,
);

export const selectMultiITFromAvgCostConversion = createSelector(
  selectMultiITFrom,
  (from): AvgCostConversion => from.avgCostConversion,
);

export const selectMultiITFromMinTaxConversion = createSelector(
  selectMultiITFrom,
  (from): MinTaxConversion => from.minTaxConversion,
);

export const selectMultiITFromAccountNumber = createSelector(
  selectMultiITFromSelectedAccount,
  (account): string | undefined => account?.accountData?.accountNumber,
);

export const selectMultiITFromDateDisplay = createSelector(
  selectMultiITFromSelectedAccount,
  (account): string => account?.accountData?.dateDisplay || '',
);

export const selectMultiITFromAccountIsManaged = createSelector(
  selectMultiITFromSelectedAccount,
  (account): boolean => account?.accountData?.managed ?? false,
);

export const selectMultiITFromAccountAllPositions = createSelector(
  selectMultiITFromSelectedAccount,
  (account): Position[] => account?.accountData?.positions || [],
);

//Computed Selectors

export const selectMultiITTransferType = createSelector(
  selectMultiITTotalRecipients,
  selectAllMultiITToAccounts,
  (total, toAccounts): InternalTransferTypeEnum => {
    if (total === 1) {
      return toAccounts[0].percentageOfHoldings === '100'
        ? InternalTransferTypeEnum.ENTIRE_ACCOUNT
        : InternalTransferTypeEnum.PARTIAL;
    }
    return InternalTransferTypeEnum.PARTIAL;
  },
);

// A holding can be selected on the holdings page but left blank (or zeroed) for every
// recipient on the amounts page. Recipients only keep positions that have a transfer
// amount, so matching against their position ids drops those holdings.
export const selectMultiITHoldingsBeingTransferred = createSelector(
  selectMultiITFromSelectedHoldings,
  selectAllMultiITToAccounts,
  (selectedHoldings, toAccounts): CostBasisPosition[] => {
    if (!selectedHoldings?.length || !toAccounts?.length) {
      return [];
    }

    const transferredPositionIds = new Set(
      toAccounts.flatMap((account) =>
        (account.positions ?? []).map((position) => position.positionId),
      ),
    );
    return selectedHoldings.filter((holding) => transferredPositionIds.has(holding.positionId));
  },
);

export const selectHasAvgCostHoldings = createSelector(
  selectMultiITHoldingsBeingTransferred,
  (holdings): boolean => {
    return holdings.some(
      (holding) => holding.selectedCostBasisMethod === CostBasisMethods.AVERAGE_COST,
    );
  },
);

export const selectHasMinTaxHoldings = createSelector(
  selectMultiITHoldingsBeingTransferred,
  (holdings): boolean => {
    return holdings.some((holding) => holding.selectedCostBasisMethod === CostBasisMethods.MIN_TAX);
  },
);

export const selectMultiITTotalTransferAmount = createSelector(
  selectAllMultiITToAccounts,
  (accounts): number => {
    if (!accounts?.length) {
      return 0;
    }

    // Sum the per-position rounded estValueOfTransfer so the total matches the
    // sum of the displayed accordion subtotals (round-then-sum, not sum-then-round).
    const total = accounts.reduce(
      (sum, account) => sum + getEstimatedTotalTransferValue(account.positions ?? []),
      0,
    );
    return Number(total.toFixed(2));
  },
);

export const selectMultiITAllAccountsHavePositions = createSelector(
  selectAllMultiITToAccounts,
  selectMultiITFromTransferMethod,
  selectCwpMultiItMoToggle,
  (accounts, transferMethod, multiItMoToggle): boolean => {
    if (!accounts?.length) {
      return false;
    }
    return accounts.every(
      (account) =>
        calculatePositionsSubtotal(account.positions, transferMethod, multiItMoToggle) > 0,
    );
  },
);

export const selectMultiITFromAccountIsVC = createSelector(
  selectMultiITFromSelectedAccount,
  (selectedAccount): boolean => selectedAccount?.accountData?.isVanguardCharitable === true,
);

export const selectMultiITIsOrgToOrg = createSelector(
  selectMultiITTriageTransferFrom,
  selectMultiITTriageTransferTo,
  (transferFrom, transferTo): boolean =>
    transferFrom === AccountTypes.ORG && !!transferTo?.includes(AccountTypes.ORG),
);

// True only when the selected FROM is a Vanguard Charitable
// account. Non-VC transfers are handled by the multi-IT submit path;
// only the VC case routes through the single-IT appian submit.
export const selectMultiITIsVCOrgToOrg = createSelector(
  selectMultiITIsOrgToOrg,
  selectMultiITFromAccountIsVC,
  (isOrgToOrg, isFromVC): boolean => isOrgToOrg && isFromVC,
);

export const selectMultiITCanOpenNewAccount = createSelector(
  selectMultiITTriageTransferFrom,
  selectMultiITTriageTransferTo,
  (transferFrom, transferTo): boolean => {
    return (
      (transferFrom === AccountTypes.INDV || transferFrom === AccountTypes.ESTATE) &&
      !!transferTo?.includes(AccountTypes.INDV)
    );
  },
);

export const selectMultiITShowMinTaxHoldings = createSelector(
  selectHasMinTaxHoldings,
  selectMultiITFromSelectedAccount,
  (hasMinTax, fromAccount): boolean => hasMinTax && !!fromAccount?.accountData?.managed,
);

export const selectIsMultiITAdditionalSignerFlow = createSelector(
  selectMultiITState,
  (state): boolean => state.mxm?.signerState != null && state.mxm?.transferId != null,
);

export const selectMultiITPositionsForTableByCostBasisMethod = (
  costBasisMethod: CostBasisMethods.AVERAGE_COST | CostBasisMethods.MIN_TAX,
) =>
  createSelector(
    selectMultiITHoldingsBeingTransferred,
    (selectedPositions): ProductListTableRow[] => {
      const filteredPositions = selectedPositions.filter(
        (position) => position.selectedCostBasisMethod === costBasisMethod,
      );

      return filteredPositions.map((position) => {
        // Fund name column - ticker (bold, underlined) + holding name (regular)
        const tickerDetails: DetailTypes[] = [
          {
            text: position.ticker,
            detailType: DETAIL_TYPE_TEXT,
            contentStyle: CONTENT_STYLE_BOLD_TICKER,
          },
        ];
        const securityNameDetails: DetailTypes[] = [
          {
            text: position.securityName,
            detailType: DETAIL_TYPE_TEXT,
            contentStyle: CONTENT_STYLE_XSMALL,
          },
        ];

        // Cost basis column
        const costBasisDetails: DetailTypes[] = [
          {
            text: costBasisMethod,
            detailType: DETAIL_TYPE_TEXT,
            contentStyle: CONTENT_STYLE_SMALL,
          },
        ];

        // Current position column - use full balance and shareQuantity
        const balanceDetails: DetailTypes[] = [
          {
            text: currencyFormatter.format(toNumber(position.balance)),
            detailType: DETAIL_TYPE_TEXT,
            contentStyle: CONTENT_STYLE_BOLD,
          },
        ];
        const quantityDetails: DetailTypes[] = [
          {
            text: `${sharesFormatter.format(toNumber(position.shareQuantity))} shares`,
            detailType: DETAIL_TYPE_TEXT,
            contentStyle: CONTENT_STYLE_SMALL,
          },
        ];

        const cells: ProductListTableCell[] = [
          {
            lines: [
              {
                details: tickerDetails,
              },
              {
                details: securityNameDetails,
              },
            ],
          },
          { lines: [{ details: costBasisDetails }] },
          { lines: [{ details: balanceDetails }, { details: quantityDetails }] },
        ];

        return { cells };
      });
    },
  );

// Built locally from the OTP reducer getter (rather than importing selectOTPVerificationStatus
// from the otp selectors barrel) to avoid a circular import.
const selectOTPStatus = createSelector(fromFeature.getOTPState, getOTPStatus);

// Only crew see the OTP modal — they verify the client's identity over the phone. A divorce
// transfer is verified through the uploaded court order instead, so it is exempt.
export const selectMultiItReviewShowOTPModal = createSelector(
  selectOTPStatus,
  selectMultiITTriageIsDivorce,
  (otpStatus, isDivorceTransfer): boolean =>
    getConfigClientFromRTE().isInternal &&
    otpStatus === OneTimePasscodeStatus.UNKNOWN &&
    isDivorceTransfer !== YesNoEmpty.YES,
);

const selectSweepTransferToggle = createSelector(
  fromFeature.getFeatureToggleState,
  getCwpCashPlusSweepTransferToggle,
);

export const selectMultiITCashPlusAccountIdsMissingEligibleHolding = createSelector(
  selectAllMultiITToAccounts,
  selectMultiITFromSelectedHoldings,
  selectSweepTransferToggle,
  (accounts, selectedHoldings, sweepTransferEnabled): string[] => {
    if (!selectedHoldings.length) return [];

    const hasEligibleHolding = selectedHoldings.some((holding) =>
      isCashPlusEligiblePosition(holding, sweepTransferEnabled),
    );
    if (hasEligibleHolding) return [];

    return accounts.reduce<string[]>((ids, account) => {
      if (
        hasAccountData(account.selectedAccount) &&
        account.selectedAccount.accountData.cashManagementAccount
      ) {
        ids.push(account.selectedAccount.accountData.accountId);
      }

      return ids;
    }, []);
  },
);

const classifyCashSweepRecipient = (account: MultiITToAccount): CashSweepRecipientType => {
  const recipient = account.selectedAccount;

  return hasAccountData(recipient) && recipient.accountData.cashManagementAccount
    ? 'cashPlus'
    : 'vba';
};

/**
 * `Cash_Plus_to_VBA` and `Cash_Plus_to_Cash_Plus` require a Cash Plus source's
 * settlement fund to be selectable, which the `cwp_cash_plus_sweep_transfer`
 * toggle controls (see eligibility.selectors.ts) — so they are only exercised in
 * environments where that toggle is enabled.
 */
export const selectMultiITCashSweepSegments = createSelector(
  selectMultiITFromSelectedAccount,
  selectAllMultiITToAccounts,
  (fromAccount, toAccounts): CashSweepSegment[] => {
    if (!hasAccountData(fromAccount)) return [];

    const recipientTypes = toAccounts
      .filter((account) => account.positions?.some((position) => position.settlementFund))
      .map(classifyCashSweepRecipient);

    const hasCashPlusRecipient = recipientTypes.includes('cashPlus');
    const hasVbaRecipient = recipientTypes.includes('vba');

    if (fromAccount.accountData.cashManagementAccount) {
      const segments: CashSweepSegment[] = [];
      if (hasCashPlusRecipient) segments.push('Cash_Plus_to_Cash_Plus');
      if (hasVbaRecipient) segments.push('Cash_Plus_to_VBA');
      return segments;
    }

    return hasCashPlusRecipient ? ['VBA_to_Cash_Plus'] : [];
  },
);

export const selectMultiITHasOptionPosition = createSelector(
  selectMultiITFromSelectedHoldings,
  (holdings): boolean => holdings.some((holding) => isOption(holding.securityType)),
);

export const selectMultiITAvailableTransferMethods = createSelector(
  selectMultiITHasOptionPosition,
  (hasOption): InternalTransferMethodDisplayEnum[] =>
    hasOption
      ? [InternalTransferMethodDisplayEnum.SHARES]
      : [InternalTransferMethodDisplayEnum.SHARES, InternalTransferMethodDisplayEnum.PERCENT],
);
